import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, Package, MapPin, RefreshCw, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const InventorySettingsPage = () => {
  const [racks, setRacks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState('all');
  
  // Add rack dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [newRackNumber, setNewRackNumber] = useState('');
  const [bulkRackNumbers, setBulkRackNumbers] = useState('');
  const [rackLocation, setRackLocation] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Edit rack
  const [editRack, setEditRack] = useState(null);
  const [editRackNumber, setEditRackNumber] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [racksRes, locationsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/enterprise/warehouse/racks`, { headers }).catch(e => ({ data: { racks: [] } })),
        axios.get(`${API_BASE_URL}/enterprise/locations`, { headers }).catch(e => ({ data: { locations: [] } }))
      ]);
      
      setRacks(racksRes.data?.racks || []);
      setLocations(locationsRes.data?.locations || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load inventory settings');
      // Set empty arrays to prevent crashes
      setRacks([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRack = async () => {
    if (bulkMode && !bulkRackNumbers.trim()) {
      toast.error('Please enter rack numbers');
      return;
    }
    if (!bulkMode && !newRackNumber.trim()) {
      toast.error('Please enter rack number');
      return;
    }
    
    // Require location selection
    if (!rackLocation || rackLocation === 'none') {
      toast.error('Please select a location');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const location = locations.find(l => l.id === rackLocation);
      
      if (bulkMode) {
        // Bulk add
        const rackNumbers = bulkRackNumbers.split('\n').map(r => r.trim()).filter(r => r);
        const response = await axios.post(
          `${API_BASE_URL}/enterprise/warehouse/racks/bulk`,
          {
            rack_numbers: rackNumbers,
            location_id: rackLocation,
            location_name: location?.name || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const createdCount = response.data?.created?.length || 0;
        const skippedCount = response.data?.skipped?.length || 0;
        toast.success(`Created ${createdCount} racks, skipped ${skippedCount} duplicates`);
      } else {
        // Single add
        await axios.post(
          `${API_BASE_URL}/enterprise/warehouse/racks`,
          {
            rack_number: newRackNumber.trim(),
            location_id: rackLocation,
            location_name: location?.name || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rack created successfully');
      }
      
      setAddDialogOpen(false);
      setNewRackNumber('');
      setBulkRackNumbers('');
      setRackLocation('');
      
      // Reload data with error handling
      try {
        await loadData();
      } catch (reloadError) {
        console.error('Failed to reload data:', reloadError);
        // Don't show error toast - the save was successful
      }
    } catch (error) {
      console.error('Failed to create rack:', error);
      toast.error(error.response?.data?.detail || 'Failed to create rack');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRack = async (rackId, rackNumber) => {
    if (!window.confirm(`Are you sure you want to delete rack "${rackNumber}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/enterprise/warehouse/racks/${rackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rack deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rack');
    }
  };

  const handleEditRack = async () => {
    if (!editRackNumber.trim()) {
      toast.error('Please enter rack number');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/enterprise/warehouse/racks/${editRack.id}?rack_number=${encodeURIComponent(editRackNumber.trim())}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rack updated');
      setEditRack(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rack');
    } finally {
      setSaving(false);
    }
  };

  const filteredRacks = selectedLocation === 'all' 
    ? racks 
    : racks.filter(r => r.location_id === selectedLocation || (!r.location_id && selectedLocation === 'none'));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-wt-text-primary mb-2">
            Inventory Settings
          </h1>
          <p className="text-wt-text-secondary">Manage warehouse racks and bin locations</p>
        </div>
        <Button 
          onClick={() => setAddDialogOpen(true)}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          data-testid="add-rack-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Racks
        </Button>
      </div>

      {/* Filter by Location */}
      <div className="flex items-center gap-4">
        <Label className="text-wt-text-primary">Filter by Location:</Label>
        <Select value={selectedLocation} onValueChange={setSelectedLocation}>
          <SelectTrigger className="w-64 bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all" className="text-wt-text-primary">All Locations</SelectItem>
            <SelectItem value="none" className="text-wt-text-primary">No Location Assigned</SelectItem>
            {locations.map(loc => (
              <SelectItem key={loc.id} value={loc.id} className="text-wt-text-primary">
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Racks Grid */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-wt-gold" />
          Warehouse Racks ({filteredRacks.length})
        </h2>
        
        {filteredRacks.length === 0 ? (
          <div className="text-center py-12 text-wt-text-muted">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No racks found. Add your first rack to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredRacks.map((rack, index) => (
              <motion.div
                key={rack?.id || `rack-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-wt-subtle border border-wt-border rounded-lg p-3 group hover:border-wt-gold/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-wt-text-primary font-mono font-semibold text-lg">
                    {rack?.rack_number || 'Unknown'}
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setEditRack(rack);
                        setEditRackNumber(rack?.rack_number || '');
                      }}
                      className="text-wt-text-muted hover:text-wt-gold p-1"
                    >
                      <Edit2 className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => rack?.id && handleDeleteRack(rack.id, rack.rack_number)}
                      className="text-wt-text-muted hover:text-red-400 p-1"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                {rack?.location_name && (
                  <div className="flex items-center gap-1 text-xs text-wt-text-muted">
                    <MapPin className="h-3 w-3" />
                    {rack.location_name}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add Rack Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Add Warehouse Racks
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Mode Toggle */}
            <div className="flex gap-2">
              <Button
                variant={!bulkMode ? 'default' : 'outline'}
                onClick={() => setBulkMode(false)}
                className={!bulkMode ? 'bg-wt-gold text-black' : 'border-wt-border text-wt-text-primary'}
                size="sm"
              >
                Single
              </Button>
              <Button
                variant={bulkMode ? 'default' : 'outline'}
                onClick={() => setBulkMode(true)}
                className={bulkMode ? 'bg-wt-gold text-black' : 'border-wt-border text-wt-text-primary'}
                size="sm"
              >
                Bulk Add
              </Button>
            </div>

            {/* Location Selection */}
            <div>
              <Label className="text-wt-text-primary">Location *</Label>
              <Select value={rackLocation || undefined} onValueChange={setRackLocation}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id} className="text-wt-text-primary">
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {bulkMode ? (
              <div>
                <Label className="text-wt-text-primary">Rack Numbers (one per line)</Label>
                <textarea
                  value={bulkRackNumbers}
                  onChange={(e) => setBulkRackNumbers(e.target.value)}
                  placeholder="A1&#10;A2&#10;A3&#10;B1&#10;B2"
                  className="w-full h-40 rounded-lg border border-wt-border bg-wt-bg px-3 py-2 text-sm text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-gold focus:outline-none font-mono"
                />
                <p className="text-xs text-wt-text-muted mt-1">
                  Enter each rack number on a new line
                </p>
              </div>
            ) : (
              <div>
                <Label className="text-wt-text-primary">Rack Number</Label>
                <Input
                  value={newRackNumber}
                  onChange={(e) => setNewRackNumber(e.target.value)}
                  placeholder="e.g., A1, SHELF-01, BIN-101"
                  className="bg-wt-bg border-wt-border text-wt-text-primary font-mono"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setAddDialogOpen(false)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddRack}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {bulkMode ? 'Add All' : 'Add Rack'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Rack Dialog */}
      <Dialog open={!!editRack} onOpenChange={(open) => !open && setEditRack(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Edit Rack</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Rack Number</Label>
              <Input
                value={editRackNumber}
                onChange={(e) => setEditRackNumber(e.target.value)}
                className="bg-wt-bg border-wt-border text-wt-text-primary font-mono"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setEditRack(null)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditRack}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventorySettingsPage;
